import { create } from 'zustand';
import { mapArtConfig, sizeDisplayToId, sizeIdToDisplay, frameDisplayToId, frameIdToDisplay, type ColorPalette } from '@/config/customizerConfig';
import {
  createPreviewJob,
  getPreviewJobStatus,
  mapPipelineStagesToFrontend,
  mapStyleToBackendId,
  mapSizeToBackendId,
  isRetriableError,
  type PreviewJobParams,
} from '@/services/previewService';

// Module-scoped abort controller for preview cancellation (not on window)
let _previewAbortController: AbortController | null = null;

// ─── v3.13.0: Three-tab preview types ───
export type PreviewTab = 'map' | 'style' | '3d';

export interface GlbGenerationParams {
  location: { lat: number; lng: number; zoom: number };
  labels: { headline: string; divider: string; tagline: string };
  style: string;
  size: string;
  palette: string;
}

// ─── v3.13.0: Mercator bbox computation (module-scoped, exported for testing) ───
export function computeBboxFromZoom(
  lat: number, lng: number, zoom: number
): [number, number, number, number] {
  const VIEWPORT_W = 600;
  const VIEWPORT_H = 500;
  const metersPerPx = 156543.03392 / Math.pow(2, zoom);
  const cosLat = Math.cos((lat * Math.PI) / 180);
  const halfW = (metersPerPx * cosLat * VIEWPORT_W) / 2;
  const halfH = (metersPerPx * cosLat * VIEWPORT_H) / 2;
  const dLat = halfH / 111_111;
  const dLng = halfW / (111_111 * cosLat);
  return [lng - dLng, lat - dLat, lng + dLng, lat + dLat];
}

// ─── v3.13.0: Stale detection (module-scoped, exported for testing) ───
export function checkStale(state: MapCustomizerState): { isStale: boolean; reason: string | null } {
  const cached = state.cachedGlbParams;
  if (!cached || !state.cachedGlbUrl) return { isStale: false, reason: null };

  const loc = state.location;
  if (loc && (cached.location.lat !== loc.lat || cached.location.lng !== loc.lng
      || cached.location.zoom !== loc.zoom)) {
    return { isStale: true, reason: 'Map location changed' };
  }
  if (cached.labels.headline !== state.labels.headline ||
      cached.labels.divider !== state.labels.divider ||
      cached.labels.tagline !== state.labels.tagline) {
    return { isStale: true, reason: 'Labels changed' };
  }
  if (cached.style !== state.selectedStyle) return { isStale: true, reason: 'Map style changed' };
  if (cached.size !== state.selectedOptions.Size) return { isStale: true, reason: 'Size changed' };
  if (cached.palette !== state.selectedPalette) return { isStale: true, reason: 'Color palette changed' };
  return { isStale: false, reason: null };
}

export interface ShopifyVariant {
  id: string;
  title: string;
  price: string;
  availableForSale: boolean;
  selectedOptions: {
    name: string;
    value: string;
  }[];
}

export interface Location {
  lat: number;
  lng: number;
  zoom: number;
  address?: string;
  boundingBox?: {
    northeast: { lat: number; lng: number };
    southwest: { lat: number; lng: number };
    northwest: { lat: number; lng: number };
    southeast: { lat: number; lng: number };
  };
}

export interface Labels {
  headline: string;
  divider: string;
  tagline: string;
  showCoordinates: boolean;
}

export interface ColorCustomization {
  mapColor: string;
  textColor: string;
  baseBoardColor: string;
}

export type PreviewState = 'idle' | 'loading' | 'result' | 'error';

export interface PreviewStage {
  id: number;
  status: 'pending' | 'active' | 'completed';
}

export interface PreviewStats {
  buildingCount: number;
  coverageKm2: number;
}

export interface MapCustomizerState {
  // Product data
  variants: ShopifyVariant[];

  // Palette data (fetched from API)
  palettes: ColorPalette[];

  // User selections
  selectedOptions: {
    Size: string;
    Frame: string;
  };
  selectedStyle: string;
  activeStyleUrl: string; // Added for dynamic map styles
  selectedPalette: string; // New: selected color palette
  hasCustomColors: boolean; // New: track if user has overridden palette colors
  labels: Labels;
  location: Location | null;
  colorCustomization: ColorCustomization;

  // Derived state
  activeVariant: ShopifyVariant | null;
  activePrice: number | null;

  // UI state
  isLoading: boolean;
  error: string | null;

  // 3D Preview state
  previewState: PreviewState;
  previewJobId: string | null;
  previewStages: PreviewStage[];
  previewGlbUrl: string | null;
  previewStats: PreviewStats | null;
  previewError: string | null;
  previewRetryCount: number;

  // Actions
  setVariants: (variants: ShopifyVariant[]) => void;
  setPalettes: (palettes: ColorPalette[]) => void;
  selectOption: (optionName: string, value: string) => void;
  setStyle: (style: string) => void;
  setPalette: (paletteId: string) => void; // New: set color palette
  setLabels: (labels: Partial<Labels>) => void;
  setLocation: (location: Location) => void;
  setLoading: (isLoading: boolean) => void;
  setError: (error: string | null) => void;
  setColorCustomization: (colors: Partial<ColorCustomization>) => void;

  // Three-tab preview state (v3.13.0)
  previewTab: PreviewTab;
  cachedGlbUrl: string | null;
  cachedGlbParams: GlbGenerationParams | null;
  isPreviewStale: boolean;
  staleReason: string | null;
  setPreviewTab: (tab: PreviewTab) => void;

  // Preview actions
  startPreview: () => void;
  cancelPreview: () => void;
  revertToCache: () => void;
  updatePreviewStage: (stageId: number, status: 'active' | 'completed') => void;
  setPreviewResult: (glbUrl: string, stats: PreviewStats) => void;
  setPreviewError: (error: string) => void;
  resetPreview: () => void;

  // Helper functions
  findMatchingVariant: () => ShopifyVariant | null;
  getCustomizationData: () => Record<string, unknown>;

  // Preview mode (legacy — kept for backward compat with PreviewArea)
  previewMode: '2D' | '3D';
  setPreviewMode: (mode: '2D' | '3D') => void;

  // Relief preview UI state (independent from backend previewState)
  reliefViewMode: 'map' | 'photo' | 'explore';
  setReliefViewMode: (mode: 'map' | 'photo' | 'explore') => void;

  // Map canvas snapshot for 3D preview texture
  mapSnapshot: string | null;
  setMapSnapshot: (dataUrl: string | null) => void;
}

export const useMapStore = create<MapCustomizerState>((set, get) => {
  // Shared helper: recompute stale state after mutations (v3.13.0)
  const recomputeStale = () => {
    const stale = checkStale(get());
    set({ isPreviewStale: stale.isStale, staleReason: stale.reason });
  };

  return {
  // Initial state
  variants: [],
  palettes: [],
  selectedOptions: {
    Size: 'small',  // Config ID — translated to Shopify display values for variant matching
    Frame: 'None',  // Config name — translated to Shopify display values for variant matching
  },
  selectedStyle: 'osm2world',
  activeStyleUrl: mapArtConfig.styles.find(s => s.id === 'osm2world')?.mapboxStyleUrl || 'mapbox://styles/mapbox/light-v11',
  selectedPalette: 'sage_cream', // Default palette
  hasCustomColors: false,
  labels: {
    headline: 'LOS ANGELES',
    divider: 'UNITED STATES',
    tagline: '34.052°N / 118.243°W',
    showCoordinates: true,
  },
  location: null,
  activeVariant: null,
  activePrice: null,
  isLoading: false,
  error: null,
  colorCustomization: {
    mapColor: '#B5C9B5',  // Sage & Cream palette colors
    textColor: '#3A5040',
    baseBoardColor: '#FAF5EB',
  },

  // Three-tab preview state (v3.13.0)
  previewTab: 'map' as PreviewTab,
  cachedGlbUrl: null,
  cachedGlbParams: null,
  isPreviewStale: false,
  staleReason: null,
  setPreviewTab: (tab) => set({ previewTab: tab }),

  // 3D Preview initial state
  previewState: 'idle' as PreviewState,
  previewJobId: null,
  previewStages: [
    { id: 1, status: 'pending' as const },
    { id: 2, status: 'pending' as const },
    { id: 3, status: 'pending' as const },
    { id: 4, status: 'pending' as const },
    { id: 5, status: 'pending' as const },
  ],
  previewGlbUrl: null,
  previewStats: null,
  previewError: null,
  previewRetryCount: 0,

  // Preview mode (legacy)
  previewMode: '2D' as const,
  setPreviewMode: (mode) => set({ previewMode: mode }),

  // Relief preview UI state
  reliefViewMode: 'map' as const,
  setReliefViewMode: (mode) => set({ reliefViewMode: mode }),

  // Map canvas snapshot for 3D preview texture
  mapSnapshot: null,
  setMapSnapshot: (dataUrl) => set({ mapSnapshot: dataUrl }),

  // Actions
  setVariants: (variants) => {
    set({ variants });

    // Auto-select options from the first variant if available
    if (variants.length > 0) {
      const firstVariant = variants[0];

      // Build selectedOptions from first variant, translating Shopify display values → config IDs
      const newSelectedOptions: { Size: string; Frame: string } = { Size: '', Frame: '' };

      firstVariant.selectedOptions.forEach(opt => {
        if (opt.name === 'Size') {
          newSelectedOptions.Size = sizeDisplayToId[opt.value] || opt.value;
        } else if (opt.name === 'Frame') {
          newSelectedOptions.Frame = frameDisplayToId[opt.value] || opt.value;
        }
      });

      // Only update if we have at least Size selected
      if (newSelectedOptions.Size) {
        set({
          selectedOptions: newSelectedOptions,
          activeVariant: firstVariant,
          activePrice: parseFloat(firstVariant.price),
        });
        return;
      }
    }

    // Fallback: try to find matching variant with current selections
    const state = get();
    const matchingVariant = state.findMatchingVariant();
    if (matchingVariant) {
      set({
        activeVariant: matchingVariant,
        activePrice: parseFloat(matchingVariant.price),
      });
    }
  },
  
  selectOption: (optionName, value) => {
    set((state) => ({
      selectedOptions: {
        ...state.selectedOptions,
        [optionName]: value,
      },
    }));

    // Update active variant and price
    const state = get();
    const matchingVariant = state.findMatchingVariant();
    if (matchingVariant) {
      set({
        activeVariant: matchingVariant,
        activePrice: parseFloat(matchingVariant.price),
      });
    }

    // Size changes affect GLB generation; Frame does not (CSS-only)
    if (optionName === 'Size') {
      recomputeStale();
    }
  },
  
  setStyle: (styleId) => {
    const newStyle = mapArtConfig.styles.find(s => s.id === styleId);
    if (newStyle) {
      set({
        selectedStyle: styleId,
        activeStyleUrl: newStyle.mapboxStyleUrl
      });
      recomputeStale();
    }
  },
  
  setLabels: (labels) => {
    set((state) => ({ labels: { ...state.labels, ...labels } }));
    recomputeStale();
  },
  
  setLocation: (location) => {
    const prev = get();
    set({ location });
    recomputeStale();
    // Auto-clear error when user changes location (v3.13.0)
    if (prev.previewState === 'error') {
      set({ previewState: 'idle', previewError: null, previewRetryCount: 0 });
    }
  },
  
  setLoading: (isLoading) => set({ isLoading }),
  
  setError: (error) => set({ error }),
  
  setPalettes: (palettes) => set({ palettes }),

  setPalette: (paletteId) => {
    const palette = get().palettes.find(p => p.id === paletteId);
    if (palette) {
      set({
        selectedPalette: paletteId,
        colorCustomization: palette.colors,
        hasCustomColors: false
      });
      recomputeStale();
    }
  },
  
  setColorCustomization: (colors) => set((state) => ({
    colorCustomization: { ...state.colorCustomization, ...colors },
    hasCustomColors: true // Mark that user has customized colors
  })),

  // Preview actions
  startPreview: () => {
    const state = get();

    // Cancel any existing preview
    state.cancelPreview();

    const freshStages: PreviewStage[] = [
      { id: 1, status: 'pending' },
      { id: 2, status: 'pending' },
      { id: 3, status: 'pending' },
      { id: 4, status: 'pending' },
      { id: 5, status: 'pending' },
    ];

    const abortController = new AbortController();
    _previewAbortController = abortController;

    set({
      previewState: 'loading',
      previewTab: '3d' as PreviewTab,
      previewStages: freshStages,
      previewGlbUrl: null,
      previewStats: null,
      previewError: null,
      previewJobId: null,
      reliefViewMode: 'map',
    });

    const runPreview = async () => {
      try {
        const location = get().location;
        if (!location) {
          get().setPreviewError('Please select a location first');
          return;
        }

        // v3.13.0: Use computeBboxFromZoom instead of stored boundingBox
        // This avoids stale bounding boxes when SimplePreview is unmounted
        const bbox = computeBboxFromZoom(location.lat, location.lng, location.zoom);

        const selectedStyle = get().selectedStyle;
        const backendStyleId = mapStyleToBackendId(selectedStyle);
        const backendSizeId = mapSizeToBackendId(get().selectedOptions.Size);

        if (!backendStyleId || !backendSizeId) {
          get().setPreviewError('Invalid product configuration. Please refresh and try again.');
          return;
        }

        const { headline, divider, tagline } = get().labels;

        // v3.13.0: Snapshot generation params at request time (not completion time)
        const genParams: GlbGenerationParams = {
          location: { lat: location.lat, lng: location.lng, zoom: location.zoom },
          labels: { headline, divider, tagline },
          style: selectedStyle,
          size: get().selectedOptions.Size,
          palette: get().selectedPalette,
        };

        const params: PreviewJobParams = {
          address: location.address ?? '',
          boundingBox: bbox,
          configName:
            backendStyleId === 'terrain_only'
              ? 'terrain_default-v1'
              : 'city_preview-v1',
          sizeId: backendSizeId,
          paletteId: get().selectedPalette,
          labels: { headline, divider, tagline },
        };

        const job = await createPreviewJob(params, abortController.signal);
        if (abortController.signal.aborted) return;
        set({ previewJobId: job.jobId });

        // Poll for status using while loop
        const POLL_INTERVAL = 3000;
        const MAX_POLL_DURATION = 720_000; // 12 minutes — accounts for ~5min cold start + ~5min processing
        const startTime = Date.now();

        while (!abortController.signal.aborted) {
          if (Date.now() - startTime > MAX_POLL_DURATION) {
            if (abortController.signal.aborted) return;
            get().setPreviewError(
              'This is taking unusually long. Please try again — subsequent attempts are usually faster.'
            );
            return;
          }

          const status = await getPreviewJobStatus(
            job.jobId,
            abortController.signal
          );

          // Guard against race: abort may have fired while awaiting response
          if (abortController.signal.aborted) return;

          const mappedStages = mapPipelineStagesToFrontend(
            status.stages
          );
          set({ previewStages: mappedStages });

          if (status.status === 'completed') {
            if (status.glbUrl) {
              set({
                previewState: 'result',
                previewGlbUrl: status.glbUrl,
                cachedGlbUrl: status.glbUrl,
                cachedGlbParams: genParams,
                previewStats: status.stats ?? null,
                previewRetryCount: 0,
              });
              // Recompute stale AFTER caching params — if user changed
              // params during loading, the model is already stale
              recomputeStale();
            } else {
              get().setPreviewError(
                'Preview completed but no model was generated.'
              );
            }
            return;
          }

          if (status.status === 'failed') {
            get().setPreviewError(
              status.error ?? status.error_msg ??
                'Something went wrong generating your preview.'
            );
            return;
          }

          await new Promise((r) => setTimeout(r, POLL_INTERVAL));
        }
      } catch (err: unknown) {
        if (abortController.signal.aborted) return;
        const message =
          err instanceof Error ? err.message : 'An unexpected error occurred';
        get().setPreviewError(message);
      }
    };

    runPreview();
  },

  cancelPreview: () => {
    if (_previewAbortController) {
      _previewAbortController.abort();
      _previewAbortController = null;
    }
  },

  updatePreviewStage: (stageId, status) => {
    set((state) => ({
      previewStages: state.previewStages.map((s) =>
        s.id === stageId ? { ...s, status } : s
      ),
    }));
  },

  setPreviewResult: (glbUrl, stats) => {
    set({
      previewState: 'result',
      previewGlbUrl: glbUrl,
      previewStats: stats,
    });
  },

  setPreviewError: (error) => {
    set((state) => ({
      previewState: 'error' as PreviewState,
      previewError: error,
      previewRetryCount: isRetriableError(error)
        ? state.previewRetryCount + 1
        : state.previewRetryCount,
    }));
  },

  resetPreview: () => {
    get().cancelPreview();
    // v3.13.0: Preserve cachedGlbUrl and cachedGlbParams across reset
    // This is the key change that keeps the 3D model across tab switches
    set({
      previewState: 'idle',
      previewJobId: null,
      previewStages: [
        { id: 1, status: 'pending' },
        { id: 2, status: 'pending' },
        { id: 3, status: 'pending' },
        { id: 4, status: 'pending' },
        { id: 5, status: 'pending' },
      ],
      previewGlbUrl: null,
      previewStats: null,
      previewError: null,
      previewRetryCount: 0,
      reliefViewMode: 'map',
    });
  },

  // v3.14.0: Revert to cached model during stale refresh cancel (Flow B)
  revertToCache: () => {
    get().cancelPreview();
    set({
      previewState: 'result',
      previewError: null,
      previewJobId: null,
      previewStages: [
        { id: 1, status: 'pending' },
        { id: 2, status: 'pending' },
        { id: 3, status: 'pending' },
        { id: 4, status: 'pending' },
        { id: 5, status: 'pending' },
      ],
      // cachedGlbUrl — NOT reset, old model stays visible
      // isPreviewStale — NOT reset, model remains marked as stale
    });
  },

  // Helper functions
  findMatchingVariant: () => {
    const state = get();

    if (!state.variants.length) {
      return null;
    }

    const matchingVariant = state.variants.find((variant) => {
      // Handle variants without selectedOptions
      if (!variant.selectedOptions || variant.selectedOptions.length === 0) {
        return false;
      }

      // Only check options that exist in the variant
      // This allows variants with just "Size" to match even if we have "Frame" selected
      // Translate config IDs → Shopify display values for comparison
      const matches = variant.selectedOptions.every((option) => {
        const currentValue = state.selectedOptions[option.name as keyof typeof state.selectedOptions];
        let displayValue = currentValue;
        if (option.name === 'Size') {
          displayValue = sizeIdToDisplay[currentValue] || currentValue;
        } else if (option.name === 'Frame') {
          displayValue = frameIdToDisplay[currentValue] || currentValue;
        }
        return displayValue === option.value;
      });

      // Also verify we're not missing required options
      // If variant has an option, we must have it selected
      if (matches) {
        const variantOptions = variant.selectedOptions.map(o => o.name);
        const hasAllRequiredOptions = variantOptions.every(optName => {
          const value = state.selectedOptions[optName as keyof typeof state.selectedOptions];
          return value !== undefined && value !== null && value !== '';
        });

        if (!hasAllRequiredOptions) {
          return false;
        }
      }

      return matches;
    });

    return matchingVariant || null;
  },
  
  getCustomizationData: () => {
    const state = get();

    const customizationData = {
      // Required fields for Shopify webhook
      Address: state.location?.address || '',
      Style: 'osm2world', // ✅ FIX v1.1.4.1: Frontend only supports city maps currently
      'Config Name': 'city_default-v1', // ✅ FIX v1.1.4.1: Matches backend osm2world config
      'Mapbox Zoom': String(state.location?.zoom ?? ''),
      'Bounding Box': (() => {
        if (state.location?.boundingBox) {
          return JSON.stringify({
            north: state.location.boundingBox.northeast.lat,
            south: state.location.boundingBox.southwest.lat,
            east: state.location.boundingBox.northeast.lng,
            west: state.location.boundingBox.southwest.lng,
          });
        }
        // Fallback: approximate bounding box from center coordinates (~1km radius)
        if (state.location) {
          const delta = 0.01;
          return JSON.stringify({
            north: state.location.lat + delta,
            south: state.location.lat - delta,
            east: state.location.lng + delta,
            west: state.location.lng - delta,
          });
        }
        return '{}';
      })(),
      
      // Additional customization data
      labels: state.labels,
      sizeId: state.selectedOptions.Size, // Use the size ID directly
      paletteId: state.selectedPalette,
      frame: state.selectedOptions.Frame,
      customColors: state.hasCustomColors ? state.colorCustomization : null,
    };
    
    return customizationData;
  },
};
});